import { supabase } from "../supabase";

function getOrderTrackingRef(saleId, trackingCode) {
  return trackingCode || String(saleId || "").slice(0, 8);
}

function getDescriptionBase(saleId, trackingCode) {
  return `Cobro Pedido #${getOrderTrackingRef(saleId, trackingCode)}`;
}

export async function registerImmediateStoreOrderFunds({ sale, shippingCost, trackingCode }) {
  if (!sale?.id || !sale?.organization_id) {
    return { skipped: true, reason: "missing_context" };
  }

  const organizationId = sale.organization_id;
  const shippingAmount = Number(shippingCost || 0);
  const paymentMethod = String(sale.payment_method || "");
  const grossAmount = Number(sale.total || 0);
  const netAmount = paymentMethod === "Efectivo" ? grossAmount - shippingAmount : grossAmount;

  if (netAmount <= 0) {
    return { skipped: true, reason: "non_positive_amount" };
  }

  const descriptionBase = getDescriptionBase(sale.id, trackingCode);

  if (paymentMethod === "Transferencia") {
    const { data: existing } = await supabase
      .from("movimientos_financieros")
      .select("id")
      .eq("organization_id", organizationId)
      .eq("documento_tipo", "Venta Online")
      .eq("documento_id", sale.id)
      .maybeSingle();

    if (existing?.id) {
      return { skipped: true, reason: "already_registered" };
    }

    const { data: config, error: configError } = await supabase
      .from("store_config")
      .select("default_bank_id")
      .eq("organization_id", organizationId)
      .maybeSingle();

    if (configError) throw configError;
    if (!config?.default_bank_id) {
      return { skipped: true, reason: "missing_default_bank" };
    }

    // Asiento PRIMERO (saldo_resultante null), luego ajuste ATÓMICO del banco vía
    // RPC y patch del saldo. Evita mover el banco sin asiento (plata fantasma) y
    // el lost update del read-modify-write concurrente. Bug F5.
    const { data: movement, error: insertError } = await supabase
      .from("movimientos_financieros")
      .insert([
        {
          organization_id: organizationId,
          bank_id: config.default_bank_id,
          tipo: "Ingreso",
          categoria: "Venta Online",
          monto: netAmount,
          saldo_resultante: null,
          descripcion: descriptionBase,
          fecha_operacion: new Date().toISOString(),
          conciliado: true,
          entidad_tipo: "sale",
          entidad_id: sale.id,
          documento_tipo: "Venta Online",
          documento_id: sale.id,
          is_active: true,
        },
      ])
      .select("id")
      .single();

    if (insertError) throw insertError;

    const { data: nextBalance, error: adjustError } = await supabase.rpc("adjust_bank_balance", {
      p_bank_id: config.default_bank_id,
      p_delta: netAmount,
    });
    if (adjustError) throw adjustError;

    await supabase
      .from("movimientos_financieros")
      .update({ saldo_resultante: nextBalance })
      .eq("id", movement.id);

    return { applied: true, target: "bank" };
  }

  if (paymentMethod === "Efectivo") {
    const { data: turno, error: turnoError } = await supabase
      .from("turnos_caja")
      .select("id")
      .eq("organization_id", organizationId)
      .eq("estado", "Abierto")
      .maybeSingle();

    if (turnoError) throw turnoError;

    if (turno?.id) {
      const description = `${descriptionBase} (Contraentrega)`;
      // Dedupe GLOBAL (no por turno): el cobro pudo haberse registrado en un
      // turno YA CERRADO. Filtrar por turno_id re-insertaba el ingreso en cada
      // turno nuevo. La descripción incluye el N° de pedido, así que es única.
      // `.limit(1)` en vez de `.maybeSingle()` para tolerar duplicados previos.
      const { data: existingCajaRows } = await supabase
        .from("movimientos_caja")
        .select("id")
        .eq("organization_id", organizationId)
        .eq("categoria", "Venta Online")
        .eq("descripcion", description)
        .limit(1);

      if (existingCajaRows && existingCajaRows.length > 0) {
        return { skipped: true, reason: "already_registered" };
      }

      const { error: insertCajaError } = await supabase.from("movimientos_caja").insert([
        {
          organization_id: organizationId,
          turno_id: turno.id,
          tipo: "Ingreso",
          categoria: "Venta Online",
          monto: netAmount,
          descripcion: description,
        },
      ]);

      if (insertCajaError) throw insertCajaError;

      return { applied: true, target: "cash" };
    }

    const { data: existingFallback } = await supabase
      .from("movimientos_financieros")
      .select("id")
      .eq("organization_id", organizationId)
      .eq("documento_tipo", "Venta Online")
      .eq("documento_id", sale.id)
      .maybeSingle();

    if (existingFallback?.id) {
      return { skipped: true, reason: "already_registered" };
    }

    const { error: insertFallbackError } = await supabase.from("movimientos_financieros").insert([
      {
        organization_id: organizationId,
        tipo: "Ingreso",
        categoria: "Venta Online Efectivo",
        monto: netAmount,
        descripcion: `${descriptionBase} (Sin turno caja)`,
        fecha_operacion: new Date().toISOString(),
        entidad_tipo: "sale",
        entidad_id: sale.id,
        documento_tipo: "Venta Online",
        documento_id: sale.id,
        is_active: true,
      },
    ]);

    if (insertFallbackError) throw insertFallbackError;

    return { applied: true, target: "financial_fallback" };
  }

  return { skipped: true, reason: "unsupported_payment_method" };
}

export async function revertImmediateStoreOrderFunds({
  organizationId,
  saleId,
  trackingCode,
}) {
  if (!organizationId || !saleId) {
    return { skipped: true, reason: "missing_context" };
  }

  let reversedCount = 0;

  const { data: financialMovements, error: financialError } = await supabase
    .from("movimientos_financieros")
    .select("id, bank_id, monto, descripcion")
    .eq("organization_id", organizationId)
    .eq("documento_tipo", "Venta Online")
    .eq("documento_id", saleId)
    .neq("is_active", false);

  if (financialError) throw financialError;

  for (const movement of financialMovements || []) {
    const movementAmount = Math.max(0, Number(movement?.monto) || 0);

    if (movement?.bank_id && movementAmount > 0) {
      // Reversa ATÓMICA del saldo (lock de fila) en vez de read-modify-write. Bug F5.
      const { error: adjustError } = await supabase.rpc("adjust_bank_balance", {
        p_bank_id: movement.bank_id,
        p_delta: -movementAmount,
      });
      if (adjustError) throw adjustError;
    }

    const nextDescription = String(movement.descripcion || "").startsWith("(ANULADO)")
      ? movement.descripcion
      : `(ANULADO) ${movement.descripcion || ""}`.trim();

    const { error: updateMovementError } = await supabase
      .from("movimientos_financieros")
      .update({
        is_active: false,
        monto: 0,
        descripcion: nextDescription,
      })
      .eq("id", movement.id);

    if (updateMovementError) throw updateMovementError;
    reversedCount += 1;
  }

  const descriptionBase = getDescriptionBase(saleId, trackingCode);
  const { data: cashMovements, error: cashError } = await supabase
    .from("movimientos_caja")
    .select("id, descripcion")
    .eq("organization_id", organizationId)
    .eq("categoria", "Venta Online")
    .ilike("descripcion", `${descriptionBase}%`);

  if (cashError) throw cashError;

  for (const movement of cashMovements || []) {
    if (String(movement.descripcion || "").includes("(ANULADO)")) continue;

    const { error: updateCashError } = await supabase
      .from("movimientos_caja")
      .update({
        descripcion: `(ANULADO) ${movement.descripcion || ""}`.trim(),
      })
      .eq("id", movement.id);

    if (updateCashError) throw updateCashError;
    reversedCount += 1;
  }

  if (!reversedCount) {
    return { skipped: true, reason: "no_registered_funds" };
  }

  return { applied: true, reversedCount };
}
